import React, { useEffect, useRef, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/Panel";
import { ModelosAgrupados } from "@/components/ModelosAgrupados";
import { CatalogosPorModelo } from "@/components/CatalogosPorModelo";
import { CargaMasivaCatalogo } from "@/components/CargaMasivaCatalogo";
import { SelectorModeloDialog } from "@/components/SelectorModeloDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Languages } from "lucide-react";

export default function CatalogoReferencia() {
  const { isAdmin } = useAuth();
  const [lotes, setLotes] = useState([]);
  const [frtResumen, setFrtResumen] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [modeloIds, setModeloIds] = useState([]);
  const [archivo, setArchivo] = useState(null);
  const [archivoFrt, setArchivoFrt] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [subiendoFrt, setSubiendoFrt] = useState(false);
  const fileRef = useRef(null);
  const fileFrtRef = useRef(null);

  const cargar = () => {
    api.get("/catalogo-referencia/lotes").then((r) => setLotes(r.data)).catch(() => {});
    api.get("/frt/resumen").then((r) => setFrtResumen(r.data)).catch(() => {});
  };

  const cargarModelos = () => api.get("/modelos").then((r) => setModelos(r.data)).catch(() => {});

  useEffect(() => {
    cargar();
    cargarModelos();
  }, []);

  const [loteAsignar, setLoteAsignar] = useState(null);
  const [asignandoLote, setAsignandoLote] = useState(false);

  const asignarModeloALote = async (modelo, esNuevo) => {
    if (esNuevo) await cargarModelos();
    setAsignandoLote(true);
    try {
      const doPut = (confirmado) =>
        api.put(`/catalogo-referencia/lotes/${loteAsignar.id}/modelo`,
          { modelo_id: modelo.id, reemplazar_confirmado: confirmado });
      let { data } = await doPut(false);
      if (data.requiere_confirmacion) {
        const nombres = data.lotes_afectados.map((x) => x.nombre_archivo).join(", ");
        const ok = window.confirm(
          `El modelo "${data.modelo_nombre}" ya tiene catálogo cargado (${nombres}). ` +
          `Se reemplazará ese catálogo por el de "${loteAsignar.nombre_archivo}". ¿Continuar?`);
        if (!ok) { setAsignandoLote(false); return; }
        ({ data } = await doPut(true));
      }
      toast.success(`Catálogo enlazado a ${data.modelo_nombre}` +
        (data.reemplazo ? ` (reemplazó ${data.lotes_reemplazados} lote/s)` : "") +
        (data.frt_enlazados ? ` · ${data.frt_enlazados} FRT enlazados` : ""));
      setLoteAsignar(null);
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAsignandoLote(false); }
  };

  const subirRepuestos = async () => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      fd.append("modelo_ids", modeloIds.join(","));
      const { data } = await api.post("/catalogo-referencia/importar-csv", fd, { timeout: 90000 });
      toast.success(`Repuestos importados: ${data.filas}`);
      setArchivo(null); setModeloIds([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      if (e.code === "ECONNABORTED" || /timeout/i.test(e.message || "")) {
        toast.warning("No se pudo confirmar la importación (tardó demasiado). La lista se refrescará: verifique si los datos aparecen abajo.");
      } else {
        toast.error(errorMsg(e));
      }
    } finally {
      setSubiendo(false);
      cargar();
    }
  };

  const subirFrt = async () => {
    setSubiendoFrt(true);
    try {
      const fd = new FormData();
      fd.append("file", archivoFrt);
      const { data } = await api.post("/frt/importar-csv", fd, { timeout: 90000 });
      toast.success(`FRT importado: ${data.creados} nuevas, ${data.actualizados} actualizadas`);
      if (data.auto_enlazados?.length)
        toast.info(`Auto-enlazados (heredados del catálogo de repuestos): ${data.auto_enlazados.join(", ")}`);
      if (data.sin_enlace?.length)
        toast.warning(`Sin modelo asignado (asígnelos abajo): ${data.sin_enlace.join(", ")}`);
      if (data.errores.length) toast.warning(`${data.errores.length} filas FRT con avisos`);
      setArchivoFrt(null);
      if (fileFrtRef.current) fileFrtRef.current.value = "";
    } catch (e) {
      if (e.code === "ECONNABORTED" || /timeout/i.test(e.message || "")) {
        toast.warning("No se pudo confirmar la importación (tardó demasiado). La lista se refrescará: verifique si los datos aparecen abajo.");
      } else {
        toast.error(errorMsg(e));
      }
    } finally {
      setSubiendoFrt(false);
      cargar();
    }
  };

  const [asignando, setAsignando] = useState(null);
  const [asignIds, setAsignIds] = useState([]);

  const [archivoTrad, setArchivoTrad] = useState(null);
  const [previewTrad, setPreviewTrad] = useState(null);
  const [cargandoTrad, setCargandoTrad] = useState(false);
  const [aplicandoTrad, setAplicandoTrad] = useState(false);
  const fileTradRef = useRef(null);

  const previsualizarTraducciones = async () => {
    setCargandoTrad(true);
    try {
      const fd = new FormData();
      fd.append("file", archivoTrad);
      const { data } = await api.post("/catalogo-referencia/traducciones/preview", fd);
      setPreviewTrad(data);
    } catch (e) { toast.error(errorMsg(e)); setPreviewTrad(null); }
    finally { setCargandoTrad(false); }
  };

  const aplicarTraducciones = async () => {
    if (!window.confirm(`¿Aplicar ${previewTrad.lineas_a_actualizar} traducciones al catálogo? Esta acción sobrescribe el campo "nombre".`)) return;
    setAplicandoTrad(true);
    try {
      const fd = new FormData();
      fd.append("file", archivoTrad);
      const { data } = await api.post("/catalogo-referencia/traducciones/aplicar", fd);
      toast.success(`Traducciones aplicadas: ${data.lineas_actualizadas} líneas actualizadas`);
      setArchivoTrad(null); setPreviewTrad(null);
      if (fileTradRef.current) fileTradRef.current.value = "";
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAplicandoTrad(false); }
  };

  const guardarAsignacion = async () => {
    try {
      await api.put("/frt/asignar-modelos", { modelo_texto: asignando, modelo_ids: asignIds });
      toast.success("Modelos asignados al FRT");
      setAsignando(null);
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const [previewAsigModelos, setPreviewAsigModelos] = useState(null);
  const [cargandoAsigModelos, setCargandoAsigModelos] = useState(false);
  const [aplicandoAsigModelos, setAplicandoAsigModelos] = useState(false);

  const previsualizarAsignarModelos = async () => {
    setCargandoAsigModelos(true);
    try {
      const { data } = await api.post("/repuestos/asignar-modelos-catalogo/preview");
      setPreviewAsigModelos(data);
    } catch (e) { toast.error(errorMsg(e)); setPreviewAsigModelos(null); }
    finally { setCargandoAsigModelos(false); }
  };

  const aplicarAsignarModelos = async () => {
    if (!window.confirm(`¿Agregar ${previewAsigModelos.modelos_agregados} modelos a ${previewAsigModelos.repuestos_a_actualizar} repuestos según el catálogo de referencia? Solo se agregan modelos, nunca se quitan.`)) return;
    setAplicandoAsigModelos(true);
    try {
      const { data } = await api.post("/repuestos/asignar-modelos-catalogo/aplicar");
      toast.success(`Modelos asignados: ${data.repuestos_actualizados} repuestos, ${data.modelos_agregados} modelos agregados`);
      setPreviewAsigModelos(null);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAplicandoAsigModelos(false); }
  };

  const eliminarFrt = async (m) => {
    if (!window.confirm(`¿Eliminar las ${m.operaciones} operaciones FRT de "${m.modelo_texto || "(sin texto)"}"?`)) return;
    try {
      await api.delete("/frt", { params: { modelo_texto: m.modelo_texto } });
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (l) => {
    if (!window.confirm(`¿Eliminar el lote "${l.nombre_archivo}" (${l.filas} filas)?`)) return;
    try {
      await api.delete(`/catalogo-referencia/lotes/${l.id}`);
      toast.success("Lote eliminado");
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const descargarLote = async (l) => {
    try {
      const r = await api.get(`/catalogo-referencia/lotes/${l.id}/descargar`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = l.nombre_archivo || "catalogo.csv"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="catalogo-referencia-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          Catálogo de referencia Honda
          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-600">Experimental</span>
        </h1>
        <p className="text-sm text-muted-foreground">
          Catálogo solo de consulta (no se cotiza). La IA lo usa en la Solicitud para sugerir códigos de
          parte cuando el repuesto no existe en el catálogo de trabajo.
        </p>
      </div>

      <CargaMasivaCatalogo modelos={modelos} onDone={cargar} onModelosChange={cargarModelos} />

      <Panel titulo="Importar catálogo de repuestos (CSV)" testId="catalogo-ref-importar-panel">
        <div className="max-w-xl space-y-3">
          <div className="space-y-1">
            <Label>CSV de repuestos *</Label>
            <p className="text-xs text-slate-500">
              Columnas: <code className="rounded bg-slate-100 px-1">modelo, categoria, es_plastico, grupo, codigo_parte, nombre, color, nombre_original, cantidad</code>
            </p>
            <p className="text-xs text-slate-500">La columna <code className="rounded bg-slate-100 px-1">cantidad</code> es opcional (cuántas piezas usa esa moto); si no viene, se usa 1.</p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" data-testid="catalogo-ref-file-input"
              onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100" />
          </div>
          <div className="space-y-1">
            <Label>Modelos del sistema a los que corresponde *</Label>
            <ModelosAgrupados modelos={modelos} seleccionados={modeloIds} testPrefix="catalogo-ref-modelo"
              onChange={setModeloIds} emptyMsg="No hay modelos registrados." />
          </div>
          <Button data-testid="catalogo-ref-importar-btn" onClick={subirRepuestos}
            disabled={subiendo || !archivo || modeloIds.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Upload size={15} className="mr-1.5" /> {subiendo ? "Importando..." : "Importar repuestos"}
          </Button>
        </div>
      </Panel>

      <Panel titulo="Importar tiempos FRT / mano de obra (CSV)" testId="catalogo-ref-frt-importar-panel">
        <div className="max-w-xl space-y-3">
          <div className="space-y-1">
            <Label>CSV de tiempos FRT *</Label>
            <p className="text-xs text-slate-500">
              Columnas: <code className="rounded bg-slate-100 px-1">modelo, seccion, operacion, alcance, frt_horas, incluye, operacion_original, codigo_parte</code>. Reimportar actualiza sin duplicar.
            </p>
            <p className="text-xs text-slate-500">
              Los modelos del sistema se enlazan solos: si ya existe un catálogo de repuestos importado con el
              mismo texto de <code className="rounded bg-slate-100 px-1">modelo</code>, el FRT hereda esos mismos modelos.
              Las líneas sin coincidencia quedan pendientes de asignación manual en la tabla de abajo.
            </p>
            <input ref={fileFrtRef} type="file" accept=".csv,text/csv" data-testid="catalogo-ref-frt-input"
              onChange={(e) => setArchivoFrt(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-amber-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-amber-700 hover:file:bg-amber-100" />
          </div>
          <Button data-testid="catalogo-ref-frt-importar-btn" onClick={subirFrt}
            disabled={subiendoFrt || !archivoFrt}
            className="bg-amber-600 hover:bg-amber-700 text-white">
            <Upload size={15} className="mr-1.5" /> {subiendoFrt ? "Importando..." : "Importar FRT"}
          </Button>
        </div>
      </Panel>

      {isAdmin && (
        <Panel titulo="Asignar modelos desde catálogo" testId="catalogo-ref-asignar-modelos-panel">
          <div className="max-w-xl space-y-3">
            <p className="text-xs text-slate-500">
              Revisa TODOS los repuestos y, por código de parte exacto, agrega los modelos que aparecen
              en el catálogo de referencia pero faltan en "Modelos compatibles" del repuesto. Nunca quita
              modelos ya asignados a mano.
            </p>
            <Button data-testid="catalogo-ref-asignar-modelos-preview-btn" onClick={previsualizarAsignarModelos}
              disabled={cargandoAsigModelos}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {cargandoAsigModelos ? "Analizando..." : "Revisar asignación de modelos"}
            </Button>

            {previewAsigModelos && (
              <div data-testid="catalogo-ref-asignar-modelos-preview" className="rounded-lg border border-slate-200 p-3 space-y-2 text-sm">
                <p className="text-slate-700">
                  <strong>{previewAsigModelos.repuestos_a_actualizar}</strong> repuestos recibirían modelos nuevos ·{" "}
                  <strong>{previewAsigModelos.modelos_agregados}</strong> modelos se agregarían en total
                </p>
                <Button data-testid="catalogo-ref-asignar-modelos-aplicar-btn" onClick={aplicarAsignarModelos}
                  disabled={aplicandoAsigModelos || previewAsigModelos.repuestos_a_actualizar === 0}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  {aplicandoAsigModelos ? "Aplicando..." : `Confirmar y asignar (${previewAsigModelos.repuestos_a_actualizar})`}
                </Button>
              </div>
            )}
          </div>
        </Panel>
      )}

      {isAdmin && (
        <Panel titulo="Aplicar traducciones al catálogo" testId="catalogo-ref-traducciones-panel">
          <div className="max-w-xl space-y-3">
            <div className="space-y-1">
              <Label>CSV de traducciones *</Label>
              <p className="text-xs text-slate-500">
                Columnas: <code className="rounded bg-slate-100 px-1">nombre_original,nombre_es</code>.
                Sobrescribe el campo "nombre" de las líneas cuyo <code className="rounded bg-slate-100 px-1">nombre_original</code>
                coincida exacto, y guarda el diccionario para que las próximas importaciones entren ya traducidas.
              </p>
              <input ref={fileTradRef} type="file" accept=".csv,text/csv" data-testid="catalogo-ref-traducciones-input"
                onChange={(e) => { setArchivoTrad(e.target.files?.[0] || null); setPreviewTrad(null); }}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100" />
            </div>
            <Button data-testid="catalogo-ref-traducciones-preview-btn" onClick={previsualizarTraducciones}
              disabled={cargandoTrad || !archivoTrad}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Languages size={15} className="mr-1.5" /> {cargandoTrad ? "Analizando..." : "Aplicar traducciones"}
            </Button>

            {previewTrad && (
              <div data-testid="catalogo-ref-traducciones-preview" className="rounded-lg border border-slate-200 p-3 space-y-2 text-sm">
                <p className="text-slate-700">
                  <strong>{previewTrad.filas_csv}</strong> filas en el CSV ·{" "}
                  <strong>{previewTrad.filas_con_coincidencia}</strong> con coincidencia ·{" "}
                  <strong>{previewTrad.lineas_a_actualizar}</strong> líneas a actualizar
                </p>
                {previewTrad.muestra.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {previewTrad.muestra.map((m, i) => (
                      <p key={i} data-testid={`catalogo-ref-traducciones-muestra-${i}`} className="text-xs text-slate-500 truncate">
                        <span className="text-slate-400">{m.nombre_actual}</span> → <span className="font-medium text-emerald-700">{m.nombre_es}</span>
                      </p>
                    ))}
                  </div>
                )}
                <Button data-testid="catalogo-ref-traducciones-aplicar-btn" onClick={aplicarTraducciones}
                  disabled={aplicandoTrad || previewTrad.lineas_a_actualizar === 0}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  {aplicandoTrad ? "Aplicando..." : `Confirmar y aplicar (${previewTrad.lineas_a_actualizar})`}
                </Button>
              </div>
            )}
          </div>
        </Panel>
      )}

      <Panel titulo="Catálogos y FRT por modelo" testId="catalogo-ref-lotes-panel">
        <CatalogosPorModelo modelos={modelos} lotes={lotes} frtResumen={frtResumen}
          onEliminarLote={eliminar} onEliminarFrt={eliminarFrt}
          onDescargarLote={descargarLote}
          onAsignarLote={(l) => setLoteAsignar(l)}
          onAsignarFrt={(m) => {
            setAsignando(m.modelo_texto);
            setAsignIds(m.modelo_ids?.length ? m.modelo_ids : (m.sugeridos || []));
          }} />
      </Panel>

      <Dialog open={asignando !== null} onOpenChange={(o) => { if (!o) setAsignando(null); }}>
        <DialogContent className="max-h-[90vh] flex flex-col" data-testid="catalogo-ref-frt-asignador-dialog">
          <DialogHeader>
            <DialogTitle>Asignar modelos al FRT</DialogTitle>
            <DialogDescription>
              Modelos del sistema para el FRT de "{asignando}" (puede marcar varios, ej. CARGO y WS).
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <ModelosAgrupados modelos={modelos} seleccionados={asignIds} testPrefix="catalogo-ref-frt-asig"
              onChange={setAsignIds} emptyMsg="No hay modelos registrados." />
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setAsignando(null)}>Cancelar</Button>
            <Button data-testid="catalogo-ref-frt-asig-guardar" onClick={guardarAsignacion}>Guardar asignación</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SelectorModeloDialog abierto={!!loteAsignar} onCerrar={() => setLoteAsignar(null)}
        modelos={modelos} guardando={asignandoLote} onSeleccionar={asignarModeloALote}
        titulo={loteAsignar ? `Asignar modelo a "${loteAsignar.nombre_archivo}"` : "Asignar modelo"}
        descripcion="Todo el catálogo de este archivo quedará enlazado a un modelo. Si el modelo ya tiene catálogo, se te pedirá confirmar el reemplazo." />
    </div>
  );
}
